import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import i18n from '../../../i18n';

interface FAQItem {
  q: string;
  a: string;
  category: 'formation' | 'credit' | 'funding';
}

const CATEGORIES = {
  formation: { labelEn: 'LLC Formation', labelEs: 'Formación LLC', icon: 'ri-building-4-line', color: 'text-[#4F2D63] bg-[#F8F3FB]' },
  credit: { labelEn: 'Credit Building', labelEs: 'Crédito Empresarial', icon: 'ri-line-chart-line', color: 'text-[#0F766E] bg-[#F0FDF4]' },
  funding: { labelEn: 'Funding Access', labelEs: 'Acceso al Capital', icon: 'ri-funds-line', color: 'text-amber-700 bg-amber-50' },
};

export default function LLCFAQSection() {
  const { t } = useTranslation();
  const isEs = i18n.language === 'es';
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [activeCategory, setActiveCategory] = useState<'all' | 'formation' | 'credit' | 'funding'>('all');

  const items: FAQItem[] = [
    { q: t('sllc_faq_1_q'), a: t('sllc_faq_1_a'), category: 'formation' },
    { q: t('sllc_faq_2_q'), a: t('sllc_faq_2_a'), category: 'formation' },
    { q: t('sllc_faq_3_q'), a: t('sllc_faq_3_a'), category: 'funding' },
    { q: t('sllc_faq_4_q'), a: t('sllc_faq_4_a'), category: 'credit' },
    { q: t('sllc_faq_5_q'), a: t('sllc_faq_5_a'), category: 'funding' },
    { q: t('sllc_faq_6_q'), a: t('sllc_faq_6_a'), category: 'formation' },
    { q: t('sllc_faq_7_q'), a: t('sllc_faq_7_a'), category: 'funding' },
    { q: t('sllc_faq_8_q'), a: t('sllc_faq_8_a'), category: 'formation' },
    { q: t('sllc_faq_9_q'), a: t('sllc_faq_9_a'), category: 'credit' },
    { q: t('sllc_faq_10_q'), a: t('sllc_faq_10_a'), category: 'formation' },
  ];

  const filtered = activeCategory === 'all' ? items : items.filter((i) => i.category === activeCategory);
  const categoryCounts = {
    formation: items.filter((i) => i.category === 'formation').length,
    credit: items.filter((i) => i.category === 'credit').length,
    funding: items.filter((i) => i.category === 'funding').length,
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-[#F8F3FB] text-[#4F2D63] text-xs font-semibold uppercase tracking-widest mb-4">
            {t('sllc_faq_eyebrow')}
          </span>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {t('sllc_faq_title')}
          </h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            {t('sllc_faq_subtitle')}
          </p>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {/* All */}
          <button
            onClick={() => { setActiveCategory('all'); setOpenIndex(null); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap
              ${activeCategory === 'all' ? 'bg-[#4F2D63] text-white' : 'bg-[#F8F3FB] text-gray-600 hover:bg-[#E9D9F2]'}`}
          >
            {isEs ? 'Todas' : 'All'} <span className="ml-1 text-xs opacity-70">({items.length})</span>
          </button>

          {(Object.entries(CATEGORIES) as [keyof typeof CATEGORIES, (typeof CATEGORIES)[keyof typeof CATEGORIES]][]).map(([key, cat]) => (
            <button
              key={key}
              onClick={() => { setActiveCategory(key); setOpenIndex(null); }}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap
                ${activeCategory === key ? 'bg-[#4F2D63] text-white' : 'bg-[#F8F3FB] text-gray-600 hover:bg-[#E9D9F2]'}`}
            >
              <i className={`${cat.icon} text-xs`}></i>
              {isEs ? cat.labelEs : cat.labelEn}
              <span className="text-xs opacity-70">({categoryCounts[key]})</span>
            </button>
          ))}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mb-10">
          {(Object.entries(CATEGORIES) as [keyof typeof CATEGORIES, (typeof CATEGORIES)[keyof typeof CATEGORIES]][]).map(([key, cat]) => (
            <div key={key} className={`rounded-xl p-4 text-center ${cat.color} border border-current/10`}>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg mx-auto mb-2">
                <i className={`${cat.icon} text-base`}></i>
              </div>
              <p className="text-2xl font-bold mb-0.5">{categoryCounts[key]}</p>
              <p className="text-xs font-medium">{isEs ? cat.labelEs : cat.labelEn}</p>
            </div>
          ))}
        </div>

        {/* Accordion */}
        <div className="space-y-2">
          {filtered.map((item, idx) => {
            const catInfo = CATEGORIES[item.category];
            return (
              <div
                key={`${activeCategory}-${idx}`}
                className={`rounded-xl border transition-all duration-200 overflow-hidden
                  ${openIndex === idx ? 'border-[#4F2D63]/40 bg-[#F8F3FB]' : 'border-[#E9D9F2] bg-white hover:border-[#4F2D63]/30'}`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                  className="w-full flex items-center gap-4 px-6 py-4 text-left cursor-pointer"
                >
                  {/* Category dot */}
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.category === 'formation' ? 'bg-[#4F2D63]' : item.category === 'credit' ? 'bg-[#0F766E]' : 'bg-amber-500'}`} />

                  <span className="flex-1 font-semibold text-gray-900 text-sm leading-relaxed">
                    {item.q}
                  </span>

                  <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors
                    ${openIndex === idx ? 'bg-[#4F2D63] text-white' : 'bg-gray-100 text-gray-400'}`}>
                    <i className={`text-sm ${openIndex === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                  </div>
                </button>

                {openIndex === idx && (
                  <div className="px-6 pb-5 pl-12">
                    <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                    {/* Category badge */}
                    <div className="mt-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${catInfo.color}`}>
                        <i className={`${catInfo.icon} text-xs`}></i>
                        {isEs ? catInfo.labelEs : catInfo.labelEn}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 bg-[#F8F3FB] rounded-2xl p-8 border border-[#E9D9F2]">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{t('faq_page_still_title')}</h3>
              <p className="text-sm text-gray-500">{t('faq_page_still_subtitle')}</p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <Link
                to={isEs ? '/es/contacto' : '/contact'}
                className="px-5 py-2.5 rounded-xl bg-[#4F2D63] text-white font-semibold text-sm hover:bg-[#3E234F] transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('faq_page_still_cta_advisor')}
              </Link>
              <Link
                to={isEs ? '/es/como-funciona' : '/how-it-works'}
                className="px-5 py-2.5 rounded-xl border border-[#E9D9F2] text-gray-700 font-semibold text-sm hover:bg-white transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('hiw_eyebrow')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
